/**
 * The workspace's side of functional blocks (D20): the save dialog, and the
 * words for what a block operation did. The operations themselves are server
 * endpoints; the editor only ever sees the draft they return.
 */
import { useId, useState } from "react";
import { ApiError, type BlockUpdate, type UpdatedBlock } from "../api/client";
import { ConfirmDialog } from "../components";
import type { GroupDefinitionDocument } from "../design/document";
import type { Point } from "../scene/types";

/** What the server said, readably: its findings when it gave reasons. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError && error.findings.length > 0) {
    return error.findings.map((finding) => finding.message).join("; ");
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * The middle of a definition's members, in its own coordinates. Placing
 * offsets every member by the given point, so dropping at `p` places at
 * `p - centre` to land the block's middle where it was dropped.
 */
export function definitionCentre(definition: GroupDefinitionDocument): Point {
  const positions = (definition.nodes ?? []).flatMap((node) => (node.schematic_position ? [node.schematic_position] : []));
  if (positions.length === 0) return { x: 0, y: 0 };
  const xs = positions.map((position) => position.x);
  const ys = positions.map((position) => position.y);
  return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
}

function list(ids: readonly string[]): string {
  return ids.length <= 4 ? ids.join(", ") : `${ids.slice(0, 4).join(", ")} and ${ids.length - 4} more`;
}

/**
 * An update is never silent (requirement 6.6): what was kept, added and
 * removed, and anything the server found, e.g. a connection that went with a
 * port the new version no longer exposes.
 */
export function describeUpdate(updated: UpdatedBlock, pending?: BlockUpdate): string {
  const versions = pending ? ` from v${pending.current_number} to v${pending.latest_number}` : "";
  const parts = [`Updated ${updated.instance_local_id}${versions}: ${updated.preserved.length} members kept`];
  if (updated.added.length > 0) parts.push(`added ${list(updated.added)}`);
  if (updated.removed.length > 0) parts.push(`removed ${list(updated.removed)}`);
  const findings = (updated.findings ?? []).map((finding) => finding.message).join(" ");
  return `${parts.join("; ")}.${findings ? ` ${findings}` : ""}`;
}

export interface SaveBlockForm {
  name: string;
  libraryScoped: boolean;
  summary: string;
}

/**
 * Save a selection as a block, choosing where it lives (requirement 8.2).
 * Project scope is the default: the shared library is the institutional
 * record (D1), so putting something there is the deliberate choice.
 */
export function SaveBlockDialog({
  count,
  busy,
  error,
  onSave,
  onCancel,
}: {
  /** Components in the selection. */
  count: number;
  busy: boolean;
  error: unknown;
  onSave: (form: SaveBlockForm) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [libraryScoped, setLibraryScoped] = useState(false);
  const [summary, setSummary] = useState("");
  const scopeName = useId();
  const valid = name.trim().length > 0 && count > 0;
  const submit = () => valid && !busy && onSave({ name: name.trim(), libraryScoped, summary: summary.trim() });
  return (
    <ConfirmDialog
      title="Save as block"
      confirmLabel="Save block"
      busyLabel="Saving…"
      danger={false}
      focusField
      busy={busy}
      confirmDisabled={!valid}
      error={error}
      onConfirm={submit}
      onCancel={onCancel}
    >
      <p style={{ margin: 0 }}>
        {count === 0
          ? "Select at least one component to make a block from."
          : `${count} component${count === 1 ? "" : "s"} and the channels between them become version 1 of a block, and the selection becomes its first instance.`}
      </p>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label>
          Name
          <input value={name} onChange={(event) => setName(event.target.value)} maxLength={200} style={{ width: "100%" }} />
        </label>
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend>Where it is kept</legend>
          <label className="row">
            <input
              type="radio"
              name={scopeName}
              checked={!libraryScoped}
              onChange={() => setLibraryScoped(false)}
            />
            This project only
          </label>
          <label className="row">
            <input type="radio" name={scopeName} checked={libraryScoped} onChange={() => setLibraryScoped(true)} />
            The shared library, for every project
          </label>
        </fieldset>
        <label>
          Summary (optional)
          <input value={summary} onChange={(event) => setSummary(event.target.value)} style={{ width: "100%" }} />
        </label>
      </form>
      <p className="muted" style={{ margin: 0 }}>
        Instances are locked to the block: change it in its own view and publish, and each instance is offered the
        update.
      </p>
    </ConfirmDialog>
  );
}
